import { isRanap } from "@/utils/unit";

export type ReadOnlyReason =
  | "editable_active"
  | "editable_grace"
  | "editable_with_approval"
  | "discharge_within_48h"
  | "discharge_over_48h"
  | "unit_lain_non_dokter";

export interface ReadOnlyResult {
  readOnly: boolean;
  reason: ReadOnlyReason;
  message: string;
  blockers: ReadOnlyReason[];
  dischargeAt?: Date;
  hoursSinceDischarge?: number;
  approvalExpiresAt?: Date;
}

export interface UnitGate {
  segmentUnitId: string | null;
  segmentUnitNama: string;
  loginUnitId: string;
  loginUnitNama: string;
  bolehLintasUnit: boolean;
}

export interface ReadOnlyOptions {
  dokumenPulang?: boolean;
}

export interface ActiveApproval {
  expiresAt: string;     // ISO 8601
  approvedAt?: string;
  grantedMinutes?: number;
  approverName?: string;
}

interface RegisterLike {
  unit?: string | null;
  dout?: string | null;
  hout?: string | number | null;
  discharge_at?: string | null;
}

const GRACE_PERIOD_MS = 48 * 3600 * 1000;

function parseDoutDate(dout: string): Date | null {
  // Backend accessor: 'd-m-Y' (e.g., '27-04-2026'), null kalau '0000-00-00'.
  const parts = dout.split("-");
  if (parts.length !== 3) return null;

  const [d, m, y] = parts.map(p => parseInt(p, 10));
  if (!d || !m || !y) return null;

  const date = new Date(y, m - 1, d, 0, 0, 0, 0);
  return isNaN(date.getTime()) ? null : date;
}

function applyHout(date: Date, hout: string | number | null | undefined): Date {
  if (hout === null || hout === undefined || hout === "" || hout === 0) {
    return date;
  }

  const raw = String(hout).trim();
  if (raw === "" || raw === "0" || raw === "0000") return date;

  let hours = 0;
  let minutes = 0;

  if (raw.includes(":")) {
    const [h, m] = raw.split(":").map(p => parseInt(p, 10));
    hours = isNaN(h) ? 0 : h;
    minutes = isNaN(m) ? 0 : m;
  } else {
    const padded = raw.padStart(4, "0");
    hours = parseInt(padded.slice(0, padded.length - 2), 10);
    minutes = parseInt(padded.slice(padded.length - 2), 10);
    if (isNaN(hours)) hours = 0;
    if (isNaN(minutes)) minutes = 0;
  }

  const result = new Date(date.getTime());
  result.setHours(hours, minutes, 0, 0);
  return result;
}

function formatExpiry(date: Date): string {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
}

function resolveDischargeAt(register: RegisterLike): Date | null {
  // Prioritas: backend accessor `discharge_at` (ISO 8601) = dout+hout.
  if (register.discharge_at) {
    const parsed = new Date(register.discharge_at);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  // Fallback: parse dout+hout mentah (payload tanpa discharge_at).
  if (!register.dout || register.dout === "0000-00-00") return null;
  const doutDate = parseDoutDate(register.dout);
  if (!doutDate) return null;
  return applyHout(doutDate, register.hout);
}

function unitTerkunci(gate?: UnitGate | null): boolean {
  return (
    !!gate &&
    !gate.bolehLintasUnit &&
    !!gate.segmentUnitId &&
    gate.segmentUnitId !== gate.loginUnitId
  );
}

function applyUnitGate(
  waktu: ReadOnlyResult,
  gate?: UnitGate | null
): ReadOnlyResult {
  const blockers: ReadOnlyReason[] = [];
  if (unitTerkunci(gate)) blockers.push("unit_lain_non_dokter");
  if (waktu.readOnly) blockers.push(waktu.reason);

  if (!blockers.includes("unit_lain_non_dokter")) {
    return { ...waktu, blockers };
  }

  const g = gate as UnitGate;

  return {
    ...waktu,
    readOnly: true,
    reason: "unit_lain_non_dokter",
    blockers,
    message:
      `Catatan pada segmen ini milik unit ${g.segmentUnitNama}. Anda login di ` +
      `${g.loginUnitNama}, sehingga segmen ini hanya dapat dibaca.` +
      (waktu.readOnly ? " Selain itu, pasien sudah pulang." : "")
  };
}

function pesanDokumenPulang(
  register: RegisterLike,
  graceEndsAt: Date
): string {
  return isRanap(register.unit)
    ? ` Resume Pulang Ranap dan Form Sebab Kematian masih dapat dibuat/diubah ` +
        `sampai ${formatExpiry(graceEndsAt)} WIB.`
    : "";
}

function evaluateWaktu(
  register: RegisterLike | null | undefined,
  now: Date,
  approval?: ActiveApproval | null,
  options?: ReadOnlyOptions
): ReadOnlyResult {
  if (!register) {
    return {
      readOnly: false,
      reason: "editable_active",
      message: "Episode pelayanan masih berlangsung.",
      blockers: []
    };
  }

  const dischargeAt = resolveDischargeAt(register);
  if (!dischargeAt) {
    return {
      readOnly: false,
      reason: "editable_active",
      message: "Episode pelayanan masih berlangsung.",
      blockers: []
    };
  }

  const deltaMs = now.getTime() - dischargeAt.getTime();
  const hoursSinceDischarge = Math.floor(deltaMs / (3600 * 1000));
  const withinGrace = deltaMs <= GRACE_PERIOD_MS;
  const graceEndsAt = new Date(dischargeAt.getTime() + GRACE_PERIOD_MS);
  const ranap = isRanap(register.unit);

  if (withinGrace && options?.dokumenPulang) {
    return {
      readOnly: false,
      reason: "editable_grace",
      message:
        `Pasien sudah pulang. Dokumen ini masih dapat dibuat/diubah sampai ` +
        `${formatExpiry(graceEndsAt)} WIB.`,
      blockers: [],
      dischargeAt,
      hoursSinceDischarge
    };
  }

  if (withinGrace && !ranap) {
    return {
      readOnly: false,
      reason: "editable_grace",
      message:
        `Pasien sudah pulang pada ${formatExpiry(dischargeAt)} WIB. Rekam ` +
        `medis masih dapat diubah dan diverifikasi tanpa pengajuan revisi ` +
        `sampai ${formatExpiry(graceEndsAt)} WIB; setelah itu terkunci dan ` +
        `perubahan harus lewat Ajukan Akses Revisi.`,
      blockers: [],
      dischargeAt,
      hoursSinceDischarge
    };
  }

  if (approval?.expiresAt) {
    const expires = new Date(approval.expiresAt);
    if (!isNaN(expires.getTime()) && expires.getTime() > now.getTime()) {
      return {
        readOnly: false,
        reason: "editable_with_approval",
        message: `Akses revisi aktif sampai ${formatExpiry(expires)} WIB.`,
        blockers: [],
        dischargeAt,
        hoursSinceDischarge,
        approvalExpiresAt: expires
      };
    }
  }

  const formatted = formatExpiry(dischargeAt);

  if (withinGrace) {
    const pengecualian = pesanDokumenPulang(register, graceEndsAt);
    return {
      readOnly: true,
      reason: "discharge_within_48h",
      message:
        `Pasien sudah pulang pada ${formatted} WIB. Rekam medis terkunci sejak ` +
        `pasien pulang.${pengecualian} Untuk mengubah data` +
        (pengecualian ? " lain" : "") +
        ` atau melakukan verifikasi, silakan ajukan akses revisi.`,
      blockers: ["discharge_within_48h"],
      dischargeAt,
      hoursSinceDischarge
    };
  }

  return {
    readOnly: true,
    reason: "discharge_over_48h",
    message:
      `Pasien sudah pulang pada ${formatted} WIB ` +
      `(${hoursSinceDischarge} jam yang lalu). Rekam medis terkunci` +
      (ranap
        ? `, termasuk Resume Pulang Ranap dan Form Sebab Kematian karena ` +
          `periode 48 jam sudah berakhir`
        : ` karena periode 48 jam koreksi rekam medis sudah berakhir`) +
      `. Untuk mengubah data atau melakukan verifikasi, silakan ajukan akses ` +
      `revisi.`,
    blockers: ["discharge_over_48h"],
    dischargeAt,
    hoursSinceDischarge
  };
}

export type VerifikasiLockReason =
  | "belum_pulang"
  | "dibuka_approval"
  | "masa_koreksi"
  | "pulang_tanpa_approval";

export interface VerifikasiLockResult {
  locked: boolean;
  reason: VerifikasiLockReason;
  message: string;
  dischargeAt?: Date;
  approvalExpiresAt?: Date;
}

export const PESAN_VERIFIKASI_TERKUNCI =
  "Pasien sudah pulang — verifikasi memerlukan akses revisi yang disetujui.";

export function evaluateVerifikasiLock(
  register: RegisterLike | null | undefined,
  now: Date = new Date(),
  approval?: ActiveApproval | null
): VerifikasiLockResult {
  const dischargeAt = register ? resolveDischargeAt(register) : null;
  if (!dischargeAt) {
    return {
      locked: false,
      reason: "belum_pulang",
      message: "Pasien belum pulang."
    };
  }

  if (approval?.expiresAt) {
    const expires = new Date(approval.expiresAt);
    if (!isNaN(expires.getTime()) && expires.getTime() > now.getTime()) {
      return {
        locked: false,
        reason: "dibuka_approval",
        message: `Akses revisi aktif sampai ${formatExpiry(expires)} WIB.`,
        dischargeAt,
        approvalExpiresAt: expires
      };
    }
  }

  const batasKoreksi = new Date(dischargeAt.getTime() + GRACE_PERIOD_MS);
  const masihMasaKoreksi = now.getTime() <= batasKoreksi.getTime();

  if (register && masihMasaKoreksi && !isRanap(register.unit)) {
    return {
      locked: false,
      reason: "masa_koreksi",
      message:
        `Pasien sudah pulang pada ${formatExpiry(dischargeAt)} WIB. ` +
        `Verifikasi rekam medis masih dapat dilakukan tanpa pengajuan revisi ` +
        `sampai ${formatExpiry(batasKoreksi)} WIB.`,
      dischargeAt
    };
  }

  const pengecualian =
    register && masihMasaKoreksi ? pesanDokumenPulang(register, batasKoreksi) : "";

  return {
    locked: true,
    reason: "pulang_tanpa_approval",
    message:
      `Pasien sudah pulang pada ${formatExpiry(dischargeAt)} WIB. ` +
      "Verifikasi rekam medis (CPPT, Read Back, Hand Over, CPO, Rekonsiliasi " +
      "Obat, Asesmen Keperawatan, dst.) hanya dapat dilakukan setelah " +
      "pengajuan akses revisi disetujui." +
      pengecualian,
    dischargeAt
  };
}

export function evaluatePemeriksaanReadOnly(
  register: RegisterLike | null | undefined,
  now: Date = new Date(),
  approval?: ActiveApproval | null,
  unitGate?: UnitGate | null,
  options?: ReadOnlyOptions
): ReadOnlyResult {
  return applyUnitGate(
    evaluateWaktu(register, now, approval, options),
    unitGate
  );
}

export function isDischargeLock(reason: ReadOnlyReason): boolean {
  return reason === "discharge_within_48h" || reason === "discharge_over_48h";
}
